import { distanceBetween, midpoint } from "./canvasMath";
import type { Point, PointerKind, ViewportTransform } from "./canvasTypes";
import type { DrawingEngine } from "./DrawingEngine";

interface GestureStart {
  centre: Point;
  distance: number;
  viewport: ViewportTransform;
}

interface PanState {
  pointerId: number;
  lastPoint: Point;
}

export interface PalmContact {
  pointerType: string;
  width: number;
  height: number;
  pressure: number;
}

export type PointerEndReason = "up" | "cancel" | "lost-capture";

export function shouldAppendFinalPointerSample(reason: PointerEndReason): boolean {
  return reason === "up";
}

// A pen plus a broad/high-pressure touch is likely a resting palm. Small touch
// contacts are not classified as palms, preserving intentional finger input.
export function shouldIgnoreTouchAsPalm(penActive: boolean, contact: PalmContact): boolean {
  if (!penActive || contact.pointerType !== "touch") return false;
  const broadContact = Math.max(contact.width, contact.height) >= 30;
  const weightedContact = Math.max(contact.width, contact.height) >= 18 && contact.pressure > 0.65;
  return broadContact || weightedContact;
}

function pointerKind(event: PointerEvent): PointerKind {
  if (event.pointerType === "touch" || event.pointerType === "pen") {
    return event.pointerType;
  }
  return "mouse";
}

export class PointerController {
  private readonly touches = new Map<number, Point>();
  private readonly capturedPointers = new Set<number>();
  private drawingPointerId: number | null = null;
  private penPointerId: number | null = null;
  private gesture: GestureStart | null = null;
  private gestureConsumed = false;
  private pan: PanState | null = null;
  private spacePressed = false;
  private destroyed = false;

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly engine: DrawingEngine,
  ) {
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerCancel);
    canvas.addEventListener("lostpointercapture", this.onLostPointerCapture);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onWindowBlur);
  }

  public destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.engine.interruptActiveInteraction("pointer-controller-destroy");
    this.clearAllPointerState();
    this.releaseAllCaptures();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerCancel);
    this.canvas.removeEventListener("lostpointercapture", this.onLostPointerCapture);
    this.canvas.removeEventListener("wheel", this.onWheel);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onWindowBlur);
    this.spacePressed = false;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.destroyed || (!event.isPrimary && event.pointerType !== "touch")) return;
    const point = this.localPoint(event);
    this.canvas.focus({ preventScroll: true });

    if (
      shouldIgnoreTouchAsPalm(this.penPointerId !== null, {
        pointerType: event.pointerType,
        width: event.width,
        height: event.height,
        pressure: event.pressure,
      })
    ) {
      event.preventDefault();
      return;
    }

    if (event.pointerType === "touch") {
      if (this.penPointerId !== null) return;
      this.touches.set(event.pointerId, point);
      this.capture(event.pointerId);

      if (this.touches.size === 1 && !this.gestureConsumed) {
        if (this.engine.beginPointerInteraction(point, "touch", event.pressure)) {
          this.drawingPointerId = event.pointerId;
        }
      } else if (this.touches.size === 2) {
        if (this.drawingPointerId !== null) {
          this.engine.discardPointerInteraction();
          this.drawingPointerId = null;
        }
        this.gestureConsumed = true;
        this.beginGesture();
      }
      event.preventDefault();
      return;
    }

    if (event.pointerType === "pen") this.penPointerId = event.pointerId;
    const panRequested =
      event.button === 1 ||
      (event.pointerType === "mouse" && this.spacePressed && event.button === 0);

    this.capture(event.pointerId);
    if (panRequested) {
      this.pan = { pointerId: event.pointerId, lastPoint: point };
    } else if (
      event.button === 0 &&
      this.engine.beginPointerInteraction(point, pointerKind(event), event.pressure)
    ) {
      this.drawingPointerId = event.pointerId;
    }
    event.preventDefault();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (this.destroyed) return;
    const point = this.localPoint(event);

    if (event.pointerType === "touch" && this.touches.has(event.pointerId)) {
      this.touches.set(event.pointerId, point);
      if (this.gesture && this.touches.size >= 2) this.updateGesture();
      else if (this.drawingPointerId === event.pointerId) {
        this.addCoalescedStrokePoints(event);
      }
      event.preventDefault();
      return;
    }

    if (this.pan?.pointerId === event.pointerId) {
      this.engine.panBy({
        x: point.x - this.pan.lastPoint.x,
        y: point.y - this.pan.lastPoint.y,
      });
      this.pan.lastPoint = point;
      event.preventDefault();
      return;
    }

    if (this.drawingPointerId === event.pointerId) {
      this.addCoalescedStrokePoints(event);
      event.preventDefault();
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.destroyed) return;
    this.finishDrawingPointer(event, "up");
    this.clearPointerState(event.pointerId);
    this.release(event.pointerId);
    event.preventDefault();
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    if (this.destroyed || !this.isTrackedPointer(event.pointerId)) return;

    // Cancellation coordinates are not reliable. Preserve only the samples
    // already received, then abandon the whole active pointer interaction so
    // a partially cancelled gesture cannot leave touch or capture state stuck.
    this.finishDrawingPointer(event, "cancel");
    this.clearAllPointerState();
    this.releaseAllCaptures();
    event.preventDefault();
  };

  private readonly onLostPointerCapture = (event: PointerEvent): void => {
    if (this.destroyed || !this.isTrackedPointer(event.pointerId)) return;

    // A normal pointerup clears state before releasing capture, so reaching
    // this branch means capture was lost unexpectedly and is treated like a
    // cancellation without sampling the lost-capture event.
    this.capturedPointers.delete(event.pointerId);
    this.finishDrawingPointer(event, "lost-capture");
    this.clearAllPointerState();
    this.releaseAllCaptures();
  };

  private readonly onWheel = (event: WheelEvent): void => {
    if (this.destroyed) return;
    event.preventDefault();
    const current = this.engine.getViewportTransform();
    const scale = Math.exp(-event.deltaY * 0.0015);
    this.engine.zoomAt(this.localPoint(event), current.zoom * scale);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === "Space" && !event.repeat) this.spacePressed = true;
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (event.code === "Space") this.spacePressed = false;
  };

  private readonly onWindowBlur = (): void => {
    this.engine.interruptActiveInteraction("window-blur");
    this.clearAllPointerState();
    this.releaseAllCaptures();
    this.spacePressed = false;
  };

  private finishDrawingPointer(event: PointerEvent, reason: PointerEndReason): void {
    if (this.drawingPointerId !== event.pointerId) return;
    if (shouldAppendFinalPointerSample(reason)) {
      this.engine.continuePointerInteraction(
        this.localPoint(event),
        pointerKind(event),
        event.pressure,
      );
    }
    if (reason === "up") this.engine.finishPointerInteraction();
    else this.engine.cancelPointerInteraction();
    this.drawingPointerId = null;
  }

  private clearPointerState(pointerId: number): void {
    if (this.pan?.pointerId === pointerId) this.pan = null;
    this.touches.delete(pointerId);
    if (this.touches.size < 2) this.gesture = null;
    if (this.touches.size === 0) this.gestureConsumed = false;
    if (this.penPointerId === pointerId) this.penPointerId = null;
  }

  private clearAllPointerState(): void {
    this.touches.clear();
    this.drawingPointerId = null;
    this.penPointerId = null;
    this.gesture = null;
    this.gestureConsumed = false;
    this.pan = null;
  }

  private isTrackedPointer(pointerId: number): boolean {
    return (
      this.capturedPointers.has(pointerId) ||
      this.drawingPointerId === pointerId ||
      this.penPointerId === pointerId ||
      this.pan?.pointerId === pointerId ||
      this.touches.has(pointerId)
    );
  }

  private addCoalescedStrokePoints(event: PointerEvent): void {
    const coalescedSamples = event.getCoalescedEvents?.();
    const samples = coalescedSamples?.length ? coalescedSamples : [event];
    for (const sample of samples) {
      this.engine.continuePointerInteraction(
        this.localPoint(sample),
        pointerKind(sample),
        sample.pressure,
      );
    }
  }

  private beginGesture(): void {
    const [first, second] = this.firstTwoTouches();
    if (!first || !second) return;
    this.gesture = {
      centre: midpoint(first, second),
      distance: Math.max(1, distanceBetween(first, second)),
      viewport: this.engine.getViewportTransform(),
    };
  }

  private updateGesture(): void {
    const [first, second] = this.firstTwoTouches();
    if (!first || !second || !this.gesture) return;
    const centre = midpoint(first, second);
    const distance = Math.max(1, distanceBetween(first, second));
    this.engine.applyGesture(
      this.gesture.viewport,
      this.gesture.centre,
      centre,
      distance / this.gesture.distance,
    );
  }

  private firstTwoTouches(): [Point | undefined, Point | undefined] {
    const values = [...this.touches.values()];
    return [values[0], values[1]];
  }

  private localPoint(event: MouseEvent | PointerEvent | WheelEvent): Point {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private capture(pointerId: number): void {
    this.capturedPointers.add(pointerId);
    try {
      this.canvas.setPointerCapture(pointerId);
    } catch {
      // Synthetic test events and older browsers may not expose capture.
    }
  }

  private release(pointerId: number): void {
    this.capturedPointers.delete(pointerId);
    try {
      if (this.canvas.hasPointerCapture(pointerId)) {
        this.canvas.releasePointerCapture(pointerId);
      }
    } catch {
      // The browser may already have released capture during cancellation.
    }
  }

  private releaseAllCaptures(): void {
    for (const pointerId of [...this.capturedPointers]) this.release(pointerId);
  }
}
